import React from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Workspace } from "../../../Models/workspace";
import { useDocumentCraftingWorkspace } from "../Common/DocumentCraftingWorkspaceContext";
import { useAllWorkspaces } from "./useAllWorkspaces";

interface Props {
  /** The workspace the module works in now (`capabilities.workspace_id`). */
  currentWorkspaceId: number | null;
}

/**
 * Platform admins act on another workspace's labels and defaults (header
 * `X-Document-Crafting-Workspace`). Switching clears the module's data and
 * remounts the page (DocumentCraftingLayout).
 */
const WorkspaceSwitcher: React.FC<Props> = ({ currentWorkspaceId }) => {
  const { t } = useTranslation("documentCrafting");
  const { workspaceId, switchWorkspace } = useDocumentCraftingWorkspace();
  const workspaces = useAllWorkspaces(true);
  const options = workspaces.data ?? [];
  const selectedId = workspaceId ?? currentWorkspaceId;
  const selected = options.find((item) => item.id === selectedId) ?? null;

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
      <Autocomplete<Workspace, false, false, false>
        options={options}
        value={selected}
        loading={workspaces.isLoading}
        onChange={(_event, workspace) => {
          if (workspace && workspace.id !== selectedId) switchWorkspace(workspace.id);
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.name}
        noOptionsText={t("settings.workspaceSwitcher.none")}
        loadingText={t("settings.workspaceSwitcher.loading")}
        sx={{ width: 280 }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t("settings.workspaceSwitcher.label")}
            placeholder={t("settings.workspaceSwitcher.placeholder")}
            helperText={t("settings.workspaceSwitcher.hint")}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {workspaces.isLoading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {workspaceId !== null && (
        <Button
          size="small"
          color="secondary"
          onClick={() => switchWorkspace(null)}
          sx={{ mb: 2.5 }}
        >
          {t("settings.workspaceSwitcher.reset")}
        </Button>
      )}
    </Box>
  );
};

export default WorkspaceSwitcher;
